/**
 * ScopeSwitch — "pilule glissante" pour basculer entre vue Perso et
 * vue Globale. Deux zones de 88 px dans un track de 34 px de hauteur
 * (rayon 17 px), fond neutre #141417, padding interne 3 px. Le côté
 * actif rend un carré arrondi (rayon 14 px, hauteur 28 px) coloré selon
 * le module (`documents`, `tasks`, `avi`). Le côté inactif reste en
 * gris neutre #5a5a5f.
 *
 * Spec 2026-02 iter81 — remplace visuellement le `HeaderPill` avec
 * `label="Vue globale"` sur Documents, Tâches, AVI. Le comportement
 * (toggle mine ↔ global) est inchangé — seul le rendu change.
 */
import UserSegment from "@/components/ui/ScopeUserSegment";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { User, Cloud } from "lucide-react";

const MODULE_STYLES = {
  documents: { bg: "#173a5c", fg: "#5aa8e0" },
  tasks:     { bg: "#3a2e10", fg: "#F9BC26" },
  // Spec iter87 (Feb 2026) — passage rose magenta → violet MS Teams
  // sur AVI. bg (fond pilule active) = #252850, fg (icône + texte) = #7b7fd4.
  avi:       { bg: "#252850", fg: "#7b7fd4" },
};

const INACTIVE_COLOR = "#5a5a5f";
const TRACK_BG = "#141417";

/**
 * @param {Object} p
 * @param {"mine"|"global"} p.value           — état courant
 * @param {(next:"mine"|"global")=>void} p.onChange
 * @param {"documents"|"tasks"|"avi"} p.module — pour la couleur du côté actif
 * @param {string} [p.testid]                  — préfixe data-testid
 * @param {string} [p.className]
 */
export default function ScopeSwitch({ value, onChange, module, subtype, testid = "scope-switch", className = "" }) {
  const { user } = useAuth();
  // Rôle Utilisateur : sur Tâches et AVI il ne voit que ses éléments → le sélecteur Perso / Globale est masqué (Documents inchangé).
  const hiddenForUser = ["tasks", "avi"].includes(module) && user && !["admin", "premier_admin"].includes(user.role);
  useEffect(() => { if (hiddenForUser && value !== "mine") onChange?.("mine"); }, [hiddenForUser, value]); // eslint-disable-line react-hooks/exhaustive-deps
  if (hiddenForUser) return null;
  const isUser = typeof value === "string" && value.startsWith("user:");
  // Spec iter164P (2026-02-27) — Sur AVI, si un `subtype` est fourni,
  // dériver la couleur du toggle du sous-type actif (spec cohérence)
  // au lieu du violet fixe.
  let style = MODULE_STYLES[module] || MODULE_STYLES.documents;
  if (module === "avi" && subtype) {
    const overrides = {
      validation:  { bg: "#252850", fg: "#8a8fe0" },
      accuse:      { bg: "#3a2610", fg: "#F9A826" },
      information: { bg: "#0f2f24", fg: "#3fc99a" },
    };
    style = overrides[subtype] || style;
  }
  const isGlobal = value === "global";
  const isMine = !isGlobal && !isUser;

  const zoneCommon = {
    width: 88,
    height: 28,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 180ms ease, color 180ms ease",
    userSelect: "none",
    letterSpacing: 0.1,
  };

  return (
    <div
      role="tablist"
      aria-label="Portée de vue"
      className={`inline-flex items-center ${className}`}
      style={{
        height: 34,
        borderRadius: 17,
        background: TRACK_BG,
        padding: 3,
        border: "1px solid rgba(255,255,255,0.04)",
      }}
      data-testid={testid}
    >
      <div
        role="tab"
        aria-selected={isMine}
        tabIndex={0}
        onClick={() => { if (!isMine) onChange?.("mine"); }}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isMine) { e.preventDefault(); onChange?.("mine"); } }}
        style={{
          ...zoneCommon,
          background: isMine ? style.bg : "transparent",
          color:      isMine ? style.fg : INACTIVE_COLOR,
        }}
        title="Vue personnelle"
        data-testid={`${testid}-mine`}
        data-active={isMine ? "true" : "false"}
      >
        <User size={14} strokeWidth={2.2} />
        <span>Perso</span>
      </div>
      <div
        role="tab"
        aria-selected={isGlobal}
        tabIndex={0}
        onClick={() => { if (!isGlobal) onChange?.("global"); }}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isGlobal) { e.preventDefault(); onChange?.("global"); } }}
        style={{
          ...zoneCommon,
          background: isGlobal ? style.bg : "transparent",
          color:      isGlobal ? style.fg : INACTIVE_COLOR,
        }}
        title="Vue globale"
        data-testid={`${testid}-global`}
        data-active={isGlobal ? "true" : "false"}
      >
        <Cloud size={14} strokeWidth={2.2} />
        <span>Globale</span>
      </div>
      <UserSegment value={value} onChange={onChange} module={module} testid={testid} />
    </div>
  );
}
